import { IconPlus, IconSearch } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { PaginationState } from "@tanstack/react-table";
import { CreateCountryDialog } from "@/components/forms/countries/create";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DebouncedInput } from "@/components/ui/input";
import { useFilters } from "@/hooks/use-filter";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, sortByToState, stateToSortBy } from "@/lib/table";
import { orpc } from "@/orpc/client";
import type { CountriesFilters } from "@/orpc/router/common/countries";
import { columns } from "@/tables/countries";

export const Route = createFileRoute("/_app/content/countries")({
	component: Page,
	validateSearch: () => ({}) as CountriesFilters,
	async loader({ context, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.common.countries.listPaginated.queryOptions({
				input: {},
			}),
		);
	},
});

function Page() {
	const { filters, setFilters } = useFilters(Route.id);

	const { data, isFetching, refetch } = useQuery(
		orpc.common.countries.listPaginated.queryOptions({
			input: filters,
			placeholderData: keepPreviousData,
		}),
	);

	const paginationState: PaginationState = {
		pageIndex: filters.pageIndex ?? DEFAULT_PAGE_INDEX,
		pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
	};
	const sortingState = sortByToState(filters.sortBy || "Name.asc");

	const toolbar = (
		<>
			<div className="relative">
				<DebouncedInput
					value={filters.q}
					onValueChange={(value) => {
						setFilters({ q: value });
					}}
					placeholder="Search Countries..."
					className="pl-10"
				/>
				<div className="pointer-events-none absolute top-0 left-2 flex h-full items-center justify-center">
					<IconSearch className="h-4" />
				</div>
			</div>
			<CreateCountryDialog>
				<Button>
					<IconPlus />
					Create a Country
				</Button>
			</CreateCountryDialog>
		</>
	);

	return (
		<>
			<SiteHeader breadcrumbs={[{ key: "content/countries", label: "Countries" }]} />
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<DataTable
							columns={columns}
							data={data?.data ?? []}
							isFetching={isFetching}
							handleReload={refetch}
							toolbar={toolbar}
							filters={filters}
							onFilterChange={(filters) => setFilters(filters)}
							paginationState={paginationState}
							onPaginationChange={(pagination) =>
								setFilters(
									typeof pagination === "function" ? pagination(paginationState) : pagination,
								)
							}
							sortingState={sortingState}
							onSortingChange={(updaterOrValue) => {
								const newSortingState =
									typeof updaterOrValue === "function"
										? updaterOrValue(sortingState)
										: updaterOrValue;
								return setFilters({ sortBy: stateToSortBy(newSortingState) });
							}}
							rowCount={data?.meta.totalItems}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
